// Registry of gallery-eligible photography, grouped by album and physical
// role. Usable items point at real files in `BackgroundImages` and carry an
// `id`, `album`, `role`, `src`, `alt`. Items where the chapter has no
// matching photograph yet are represented only by `photoRole` and `context`
// — never a fabricated `src` — so callers render a `PhotoBrief` instead of a
// broken image request.
//
// `size` ('large' | 'wide' | 'tall' | undefined) sets the tile's mosaic
// shape (see GalleryPage.scss); undefined is one small square unit. Real
// photos are sized to their actual orientation where it matters — the two
// portrait photos ('brotherhood-bonfire' 1512x2016, 'recruitment-booth'
// 899x1347) read as 'tall'/'large', the two landscape photos
// ('kappa-class-flag', 'service-trail-work', both 1932x1288) read as 'wide'
// — rather than an arbitrary size independent of what the photo shows.
//
// Every real photo here is exclusive to the gallery. Photos already doing a
// job elsewhere on the site (the homepage/About hero, AboutPillars, the
// national-history photo, the About page's stoles/graduates pair) are
// deliberately left out so the gallery never just repeats another page.

import kinshipbonfire from '../images/BackgroundImages/kinshipbonfire.JPEG';
import kappaClassGoofy from '../images/BackgroundImages/kappaclassgoofy.jpg';
import serviceTrailWork from '../images/BackgroundImages/serviceevent2.jpg';
import recruitmentBooth from '../images/BackgroundImages/recruitmentbooth.jpg';

const galleryAlbums = [
    {
        id: 'regional-convention-candid',
        album: 'Brotherhood',
        photoRole: 'Regional convention candid',
        context: 'An informal, in-the-moment shot of brothers at a regional convention.',
        size: 'wide',
    },
    {
        id: 'bid-day',
        album: 'Brotherhood',
        photoRole: 'New member bid day',
        context: 'The chapter welcoming its newest pledge class.',
    },
    {
        id: 'brotherhood-bonfire',
        album: 'Brotherhood',
        role: 'Brotherhood bonfire',
        src: kinshipbonfire,
        alt: 'Members gathered around a bonfire for a brotherhood event.',
        size: 'tall',
    },
    {
        id: 'late-night-study',
        album: 'Brotherhood',
        photoRole: 'Late-night study session',
        context: 'Brothers studying together, the kind of everyday moment that builds the community.',
        size: 'tall',
    },
    {
        id: 'kappa-class-flag',
        album: 'Brotherhood',
        role: 'Kappa Class with the chapter flag',
        src: kappaClassGoofy,
        alt: 'The Kappa pledge class posing with the chapter flag and a decorated paddle.',
        size: 'wide',
    },
    {
        id: 'community-cleanup',
        album: 'Service',
        photoRole: 'Community cleanup',
        context: 'Members picking up litter or clearing a public space, like a beach or park cleanup day.',
    },
    {
        id: 'tutoring-session',
        album: 'Service',
        photoRole: 'Tutoring or mentorship session',
        context: 'A member working one-on-one with a student as part of a service commitment.',
    },
    {
        id: 'service-trail-work',
        album: 'Service',
        role: 'Trail restoration day',
        src: serviceTrailWork,
        alt: 'Members posing with work gloves on a hillside trail after a restoration service project.',
        size: 'wide',
    },
    {
        id: 'project-team-at-work',
        album: 'Profession',
        photoRole: 'Project team at work',
        context: 'A small team of members collaborating on a professional or technical project.',
        size: 'wide',
    },
    {
        id: 'guest-speaker',
        album: 'Profession',
        photoRole: 'Guest speaker or workshop',
        context: 'Members attending a professional development workshop or guest talk.',
    },
    {
        id: 'resume-workshop',
        album: 'Profession',
        photoRole: 'Resume or interview workshop',
        context: 'Members practicing interview skills or reviewing resumes together.',
    },
    {
        id: 'recruitment-booth',
        album: 'Recruitment',
        role: 'Recruitment tabling',
        src: recruitmentBooth,
        alt: 'Members at a campus recruitment tent holding oversized chapter letters.',
        size: 'large',
    },
    {
        id: 'alumni-industry-event',
        album: 'Alumni',
        photoRole: 'Alumni at industry event',
        context: 'Alumni networking or presenting at an industry event.',
    },
    {
        id: 'alumni-homecoming',
        album: 'Alumni',
        photoRole: 'Alumni homecoming',
        context: 'Alumni returning to campus to reconnect with current members.',
        size: 'tall',
    },
];

export default galleryAlbums;
